import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit2, Trash2, Users, Mail, Phone, MapPin,
  ChevronLeft, ChevronRight, X, Save, Building2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CLIENT_TYPES = [
  { value: 'residencial', label: 'Residencial' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'gobierno', label: 'Gobierno' },
  { value: 'ong', label: 'ONG' }
];

const STATUS_CONFIG = {
  lead: { label: 'Lead', color: 'bg-zinc-500/20 text-zinc-400' },
  prospecto: { label: 'Prospecto', color: 'bg-blue-500/20 text-blue-400' },
  cliente: { label: 'Cliente', color: 'bg-emerald-500/20 text-emerald-400' },
  cliente_vip: { label: 'Cliente VIP', color: 'bg-amber-500/20 text-amber-400' },
  inactivo: { label: 'Inactivo', color: 'bg-red-500/20 text-red-400' }
};

const ClientModal = ({ client, onClose, onSave }) => {
  const [formData, setFormData] = useState(client || {
    name: '', client_type: 'residencial', is_company: false,
    company_name: '', ruc: '',
    email: '', phone: '', whatsapp: '',
    address: '', city: 'Asunción', department: 'Central',
    status: 'lead', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {client ? 'Editar Cliente' : 'Nuevo Cliente'}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Tipo */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_company}
                onChange={(e) => setFormData({ ...formData, is_company: e.target.checked })}
                className="w-4 h-4 rounded border-zinc-600 text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm text-zinc-300">Es empresa</span>
            </label>
          </div>

          {/* Info básica */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={formData.is_company ? '' : 'sm:col-span-2'}>
              <label className="block text-sm text-zinc-400 mb-1">
                {formData.is_company ? 'Nombre de Contacto *' : 'Nombre Completo *'}
              </label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            {formData.is_company && (
              <>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Razón Social *</label>
                  <input
                    value={formData.company_name || ''}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required={formData.is_company}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">RUC</label>
                  <input
                    value={formData.ruc || ''}
                    onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </>
            )}
          </div>

          {/* Contacto */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Teléfono *</label>
              <input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">WhatsApp</label>
              <input
                value={formData.whatsapp || ''}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Ubicación */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <label className="block text-sm text-zinc-400 mb-1">Dirección</label>
              <input
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Ciudad</label>
              <input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Departamento</label>
              <input
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Tipo de Cliente</label>
              <select
                value={formData.client_type}
                onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              >
                {CLIENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Estado y notas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Estado</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              >
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1">Notas</label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
            <button type="button" onClick={onClose} className="px-4 py-2 text-zinc-400 hover:text-white">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ClientsPage = () => {
  const { fetchWithAuth, hasPermission } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [page, setPage] = useState(0);
  const limit = 20;

  const canEdit = hasPermission('clients_edit');

  const loadClients = async () => {
    setLoading(true);
    try {
      let url = `/api/clients?skip=${page * limit}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await fetchWithAuth(url);
      if (res.ok) {
        const data = await res.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadClients();
  }, [page, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadClients();
  };

  const handleSaveClient = async (clientData) => {
    try {
      const url = editingClient 
        ? `/api/clients/${editingClient.id}`
        : '/api/clients';
      
      const res = await fetchWithAuth(url, {
        method: editingClient ? 'PUT' : 'POST',
        body: JSON.stringify(clientData)
      });

      if (res.ok) {
        setShowModal(false);
        setEditingClient(null);
        loadClients();
      } else {
        const error = await res.json();
        alert(error.detail || 'Error al guardar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const handleDelete = async (clientId) => {
    if (!confirm('¿Estás seguro de eliminar este cliente?')) return;
    
    try {
      const res = await fetchWithAuth(`/api/clients/${clientId}`, { method: 'DELETE' });
      if (res.ok) {
        loadClients();
      } else {
        const error = await res.json();
        alert(error.detail || 'Error al eliminar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Clientes</h1>
          <p className="text-zinc-400 text-sm">Base de datos de clientes y prospectos</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditingClient(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4" />
            Nuevo Cliente
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, teléfono, email..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
            Buscar
          </button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_CONFIG).map(([value, config]) => (
            <option key={value} value={value}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Clients Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay clientes</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client) => {
              const statusConfig = STATUS_CONFIG[client.status] || STATUS_CONFIG.lead;
              
              return (
                <div
                  key={client.id}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                        {client.is_company ? (
                          <Building2 className="w-5 h-5 text-zinc-400" />
                        ) : (
                          <Users className="w-5 h-5 text-zinc-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {client.is_company ? client.company_name : client.name}
                        </p>
                        {client.is_company && (
                          <p className="text-xs text-zinc-500">{client.name}</p>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${statusConfig.color}`}>
                      {statusConfig.label}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <p className="flex items-center gap-2 text-zinc-400">
                      <Phone className="w-4 h-4 text-zinc-500" />
                      {client.phone}
                    </p>
                    {client.email && (
                      <p className="flex items-center gap-2 text-zinc-400 truncate">
                        <Mail className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                        {client.email}
                      </p>
                    )}
                    <p className="flex items-center gap-2 text-zinc-400">
                      <MapPin className="w-4 h-4 text-zinc-500" />
                      {client.city}, {client.department}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                    <div className="text-xs text-zinc-500">
                      <span className="capitalize">{client.client_type}</span>
                      {client.total_projects > 0 && (
                        <span className="ml-2 text-emerald-400">• {client.total_projects} proyectos</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {canEdit && (
                        <button
                          onClick={() => { setEditingClient(client); setShowModal(true); }}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-500">Página {page + 1}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
              >
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={clients.length < limit}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
              >
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <ClientModal
          client={editingClient}
          onClose={() => { setShowModal(false); setEditingClient(null); }}
          onSave={handleSaveClient}
        />
      )}
    </div>
  );
};

export default ClientsPage;
